import { useCallback, useEffect, useRef } from 'react'
import type { GraphAction } from '@/components/langgraph-chat/types'
import { useAgentSidebar } from '@/components/layout/AgentSidebarContext'

/**
 * Bridges agent-dispatched GraphActions to the KnowledgeGraphView.
 *
 * The AgentSidebar dispatches GraphActions via AgentSidebarContext. CaseLayout
 * tries to forward these through InvestigateContext, but CaseLayout lives above
 * InvestigateContextProvider so that bridge is null. We register directly with
 * the AgentSidebar here so show_path / highlight_nodes / reset reach the graph.
 *
 * The returned `registerGraphActionHandler` is handed to KnowledgeGraphView's
 * `onRegisterGraphActionHandler` prop; the view calls it with its own handler,
 * which we stash in a ref and invoke whenever the AgentSidebar dispatches an
 * action.
 */
export function useKnowledgeGraphAgentBridge(): (handler: (action: GraphAction) => void) => void {
  const { registerGraphActionHandler: registerWithAgentSidebar } = useAgentSidebar()

  const graphHandlerRef = useRef<((action: GraphAction) => void) | null>(null)
  const registerGraphActionHandler = useCallback((handler: (action: GraphAction) => void) => {
    graphHandlerRef.current = handler
  }, [])

  useEffect(() => {
    registerWithAgentSidebar((action: GraphAction) => {
      graphHandlerRef.current?.(action)
    })
    return () => {
      registerWithAgentSidebar(null)
    }
  }, [registerWithAgentSidebar])

  return registerGraphActionHandler
}
